import { useEffect, useMemo, useState } from "react"
import { useInertiaCable } from "@inertia-cable/react"
import { Link, router } from "@inertiajs/react"
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Sparkles, Play, RefreshCcw } from "lucide-react"
import { toast } from "sonner"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select, type SelectOption } from "@/components/ui/select"
import { Sheet, SheetHeader, SheetBody } from "@/components/ui/sheet"
import { TestingOverviewStrip } from "./TestingOverviewStrip"
import { SkillSuiteList, type ScenarioSelection } from "./SkillSuiteList"
import { SkillTestingPanel } from "@/components/domain/skill-testing-panel"
import { RehearsalResultsTable, type Filter as RehearsalFilter } from "@/components/domain/rehearsal-results-table"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type {
  TestingProps,
  PendingPromoteCard,
  RehearsalDetail,
  SuiteHealthSkill,
} from "@/types/agent_testing"

const TEST_PROGRESS_POLL_MS = 5000
const ACTIVE_PROGRESS_STATES = new Set(["queued", "pending", "running"])

type RunView = "latest_runs" | "latest_rehearsal"
type ScenarioFilter = "all" | "failures" | "pending"

export default function TestingIndex({
  agents,
  selectedAgentId,
  pendingPromotes,
  latestRehearsal,
  agentSuiteHealth,
  overview,
  testing_stream,
}: TestingProps) {
  const [busySkillIds, setBusySkillIds] = useState<Set<number>>(new Set())
  const [agentRehearsalBusy, setAgentRehearsalBusy] = useState(false)
  const [rerunningRehearsalId, setRerunningRehearsalId] = useState<number | null>(null)
  // Per-scenario rerun button — keyed on the scenario id (not the
  // source TestRun) so the row's spinner is independent and survives
  // even when prior runs have been wiped.
  const [busyScenarioIds, setBusyScenarioIds] = useState<Set<number>>(new Set())

  // First pick the source of truth, then filter within it. The default is
  // "all latest runs" so one-off reruns do not get visually buried under
  // an older failed rehearsal.
  const [runView, setRunView] = useState<RunView>(() => {
    if (typeof window === "undefined") return "latest_runs"
    const saved = window.localStorage.getItem("valet:testing:runView")
    if (saved === "latest_runs" || saved === "latest_rehearsal") return saved
    return "latest_runs"
  })
  useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem("valet:testing:runView", runView)
  }, [runView])

  const [tab, setTab] = useState<ScenarioFilter>(() => {
    if (typeof window === "undefined") return "all"
    const saved = window.localStorage.getItem("valet:testing:scenarioFilter")
    if (saved === "all" || saved === "failures" || saved === "pending") return saved
    return "all"
  })
  useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem("valet:testing:scenarioFilter", tab)
  }, [tab])

  const [search, setSearch] = useState("")

  // Selected scenario in the workspace. Defaults to the first failing
  // scenario so the operator's first read is what's red.
  const [selection, setSelection] = useState<ScenarioSelection | null>(null)
  const firstFailingSelection = useMemo(
    () => firstFailingScenario(agentSuiteHealth),
    [agentSuiteHealth],
  )
  // Auto-pick the first failing scenario on first arrival (only if the
  // operator hasn't picked something themselves yet).
  useEffect(() => {
    if (selection != null) return
    if (firstFailingSelection) setSelection(firstFailingSelection)
  }, [firstFailingSelection, selection])

  // Resolve the selected scenario + its parent skill from props.
  const resolved = useMemo(() => {
    if (!selection) return null
    const skill = agentSuiteHealth.find((s) => s.skill_id === selection.skillId)
    if (!skill) return null
    const scenario = skill.scenarios.find((sc) => sc.id === selection.scenarioId)
    if (!scenario) return null
    return { skill, scenario }
  }, [agentSuiteHealth, selection])

  // Mobile detail Sheet visibility — open when the user picks a scenario
  // below lg. Above lg the right pane is always visible alongside the list.
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false)

  const progressPollActive = useMemo(() => {
    if (busySkillIds.size > 0 || busyScenarioIds.size > 0 || agentRehearsalBusy || rerunningRehearsalId != null) return true
    if (isActiveProgressState(latestRehearsal?.state)) return true
    if (pendingPromotes.some((card) => isActiveProgressState(card.rehearsal?.state))) return true
    return agentSuiteHealth.some((skill) =>
      skill.scenarios.some((scenario) => isActiveProgressState(scenario.latest_status)),
    )
  }, [
    agentRehearsalBusy,
    agentSuiteHealth,
    busyScenarioIds.size,
    busySkillIds.size,
    latestRehearsal?.state,
    pendingPromotes,
    rerunningRehearsalId,
  ])

  // Live rehearsal/test progress. TestRun and Rehearsal model broadcasts
  // ping the company stream as Sidekiq jobs finish; bounded polling below
  // remains as a fallback for missed cable events or background tabs.
  useInertiaCable(testing_stream, {
    only: ["pendingPromotes", "latestRehearsal", "agentSuiteHealth", "overview"],
    debounce: 500,
  })

  useEffect(() => {
    if (!progressPollActive) return

    let inFlight = false
    const poll = () => {
      if (inFlight) return
      inFlight = true
      router.reload({
        only: ["pendingPromotes", "latestRehearsal", "agentSuiteHealth", "overview"],
        onSuccess: () => {
          setBusySkillIds(new Set())
          setBusyScenarioIds(new Set())
          setAgentRehearsalBusy(false)
          setRerunningRehearsalId(null)
        },
        onFinish: () => {
          inFlight = false
        },
      })
    }

    const id = window.setInterval(poll, TEST_PROGRESS_POLL_MS)
    return () => window.clearInterval(id)
  }, [progressPollActive])

  const handleAgentChange = (agentId: number) => {
    router.get("/testing", { agent_id: agentId }, { preserveScroll: true, preserveState: false })
  }

  async function csrfPost(path: string, body?: object) {
    const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
    return fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async function toastFromError(res: Response, fallback: string) {
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    toast.error(data.error || res.statusText || fallback)
  }

  async function runRehearsal(skillId: number) {
    setBusySkillIds((prev) => new Set(prev).add(skillId))
    try {
      const res = await csrfPost(`/skills/${skillId}/rehearsals`)
      if (!res.ok) {
        await toastFromError(res, "Couldn't start rehearsal")
        setBusySkillIds((prev) => { const n = new Set(prev); n.delete(skillId); return n })
        return
      }
      toast.success("Rehearsal queued")
      router.reload({ only: ["pendingPromotes", "latestRehearsal", "overview"] })
    } catch {
      toast.error("Network error — try again")
      setBusySkillIds((prev) => { const n = new Set(prev); n.delete(skillId); return n })
    }
  }

  async function runAgentRehearsal() {
    if (!selectedAgentId) return
    setAgentRehearsalBusy(true)
    try {
      const res = await csrfPost(`/agents/${selectedAgentId}/rehearsals`)
      if (!res.ok) {
        await toastFromError(res, "Couldn't start agent rehearsal")
        setAgentRehearsalBusy(false)
        return
      }
      toast.success("Agent rehearsal queued — running active skill and integration scenarios")
      router.reload({ only: ["pendingPromotes", "latestRehearsal", "agentSuiteHealth", "overview"] })
    } catch {
      toast.error("Network error — try again")
      setAgentRehearsalBusy(false)
    }
  }

  // Run one scenario against the current skill state. Always creates a
  // fresh TestRun, regardless of whether prior runs exist — resilient
  // to scenarios with no history. Hits the scenario-level endpoint
  // (/skills/:id/scenarios/:scenario_id/run), not the run-level replay.
  async function rerunOneScenario(skillId: number | null, scenarioId: number) {
    setBusyScenarioIds((cur) => { const n = new Set(cur); n.add(scenarioId); return n })
    try {
      const path = skillId == null
        ? `/testing/scenarios/${scenarioId}/run${selectedAgentId ? `?agent_id=${selectedAgentId}` : ""}`
        : `/skills/${skillId}/scenarios/${scenarioId}/run`
      const res = await csrfPost(path)
      if (!res.ok) {
        await toastFromError(res, "Couldn't rerun scenario")
        return
      }
      toast.success("Rerunning scenario — results will refresh shortly")
      router.reload({ only: ["agentSuiteHealth", "overview", "latestRehearsal"] })
    } catch {
      toast.error("Network error — try again")
    } finally {
      setBusyScenarioIds((cur) => { const n = new Set(cur); n.delete(scenarioId); return n })
    }
  }

  async function rerunFailedRehearsal() {
    if (!latestRehearsal) return
    const skillId = latestRehearsal.target_skill_id
    if (!skillId) {
      toast.error("Rerun-failed isn't available for agent-wide rehearsals — start a new one instead.")
      return
    }
    setRerunningRehearsalId(latestRehearsal.id)
    try {
      const res = await csrfPost(`/skills/${skillId}/rehearsals/${latestRehearsal.id}/rerun_failed`)
      if (!res.ok) {
        await toastFromError(res, "Couldn't rerun failed scenarios")
        setRerunningRehearsalId(null)
        return
      }
      toast.success("Rerunning failed scenarios")
      router.reload({ only: ["latestRehearsal", "pendingPromotes", "overview"] })
    } catch {
      toast.error("Network error — try again")
      setRerunningRehearsalId(null)
    }
  }

  async function promoteDraft(skillId: number) {
    setBusySkillIds((prev) => new Set(prev).add(skillId))
    try {
      const res = await csrfPost(`/skills/${skillId}/promote`)
      if (!res.ok) {
        await toastFromError(res, "Couldn't promote draft")
        setBusySkillIds((prev) => { const n = new Set(prev); n.delete(skillId); return n })
        return
      }
      toast.success("Draft promoted")
      router.reload({ only: ["pendingPromotes", "latestRehearsal", "agentSuiteHealth", "overview"] })
    } catch {
      toast.error("Network error — try again")
      setBusySkillIds((prev) => { const n = new Set(prev); n.delete(skillId); return n })
    }
  }

  if (agents.length === 0) {
    return (
      <AppShell>
        <PageHeader title="Agent Testing" subtitle="No agents yet — create one to start testing." />
        <Card className="p-6 text-sm text-app-muted">
          You don't have any agents in this company yet.
          <Link href="/agents" className="ml-2 text-brand-red hover:underline">Create an agent →</Link>
        </Card>
      </AppShell>
    )
  }

  const rehearsalInflight = latestRehearsal?.state === "queued" || latestRehearsal?.state === "running"
  const rehearsalRunningCount = latestRehearsal?.running_count ?? 0
  const agentOptions: SelectOption<number>[] = agents.map((a) => ({ value: a.id, label: a.name }))
  const selectedAgentOption = agentOptions.find((o) => o.value === selectedAgentId) ?? null
  const failedTotal = (latestRehearsal?.failed_count ?? 0) + (latestRehearsal?.errored_count ?? 0)
  const showRerunFailed =
    runView === "latest_rehearsal"
    && latestRehearsal != null
    && (latestRehearsal.state === "failed" || latestRehearsal.state === "errored")
    && failedTotal > 0
  const failureOnly = tab === "failures"
  const rehearsalFilter: RehearsalFilter = { failureOnly, search }
  const showingDrafts = tab === "pending"
  const showingLatestRehearsal = runView === "latest_rehearsal" && !showingDrafts
  const showingLatestRuns = runView === "latest_runs" && !showingDrafts

  return (
    <AppShell>
      <PageHeader
        title="Agent Testing"
        subtitle="Review all latest scenario runs, latest rehearsal runs, and draft promotion status."
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <Button
              variant="red"
              size="sm"
              onClick={runAgentRehearsal}
              disabled={agentRehearsalBusy || rehearsalInflight || !selectedAgentId}
              className="w-full sm:w-auto"
            >
              <Play size={14} className="mr-1" />
              {agentRehearsalBusy
                ? "Starting…"
                : rehearsalInflight
                  ? `Rehearsal running${rehearsalRunningCount > 0 ? ` (${rehearsalRunningCount})` : "…"}`
                  : "Run agent rehearsal"}
            </Button>
            <Link
              href={selectedAgentId ? `/testing/integration?agent_id=${selectedAgentId}` : "/testing/integration"}
              className="inline-flex min-h-9 w-full items-center justify-center rounded-control border border-border bg-white/[0.04] px-3 text-sm font-medium text-app-text transition hover:bg-white/[0.07] sm:w-auto"
            >
              Integration bench
            </Link>
            {showRerunFailed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={rerunFailedRehearsal}
                disabled={rerunningRehearsalId === latestRehearsal?.id}
                className="w-full sm:w-auto"
              >
                <RefreshCcw size={14} className="mr-1" />
                {rerunningRehearsalId === latestRehearsal?.id
                  ? "Rerunning…"
                  : `Rerun failed rehearsal (${failedTotal})`}
              </Button>
            )}
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <label className="hidden text-[11px] uppercase tracking-wide text-app-muted sm:inline">
                Agent
              </label>
              <div className="w-full min-w-[200px] sm:w-[240px]">
                <Select<number>
                  value={selectedAgentOption}
                  options={agentOptions}
                  onChange={(opt) => opt && handleAgentChange(opt.value)}
                  isSearchable={agents.length > 6}
                  placeholder="Select agent…"
                  aria-label="Select agent"
                />
              </div>
            </div>
          </div>
        }
      />

      {/* Overview stats */}
      <TestingOverviewStrip overview={overview} />

      {/* Run source, filter, search */}
      <section className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div className="space-y-3">
          <RunViewTabs runView={runView} onChange={setRunView} latestRehearsal={latestRehearsal} />
          <TestingTabs tab={tab} onChange={setTab} pendingCount={pendingPromotes.length} />
        </div>
        {!showingDrafts && (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search scenarios…"
            className="min-h-11 w-full rounded-control border border-border bg-app-bg px-4 text-sm text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none lg:w-80"
          />
        )}
      </section>

      {showingDrafts && (
        <PendingPromotes
          cards={pendingPromotes}
          busySkillIds={busySkillIds}
          onRun={runRehearsal}
          onPromote={promoteDraft}
        />
      )}

      {showingLatestRehearsal && (
        latestRehearsal ? (
          <LatestRehearsalBlock rehearsal={latestRehearsal} filter={rehearsalFilter} />
        ) : (
          <Card className="p-6 text-sm text-app-muted">No rehearsal has run for this agent yet.</Card>
        )
      )}

      {showingLatestRuns && (
        <>
          <section className="grid gap-4 xl:grid-cols-[minmax(380px,0.78fr)_minmax(0,1.22fr)]">
            <SkillSuiteList
              skills={agentSuiteHealth}
              selected={selection}
              onSelect={(sel) => {
                setSelection(sel)
                if (typeof window !== "undefined" && window.matchMedia("(max-width: 1023.99px)").matches) {
                  setMobileDetailOpen(true)
                }
              }}
              search={search}
              failureOnly={failureOnly}
              onRerun={rerunOneScenario}
              busyScenarioIds={busyScenarioIds}
            />

            {/* Desktop: detail pane visible alongside list. */}
            <div className="hidden xl:block">
              {resolved ? (
                <SkillTestingPanel
                  key={`${resolved.skill.skill_id}-${resolved.scenario.id}`}
                  skill={resolved.skill}
                  scenario={resolved.scenario}
                />
              ) : (
                <section className="valet-panel rounded-card p-8 text-sm text-app-muted">
                  Select a skill or scenario from the list to open the testing panel.
                </section>
              )}
            </div>
          </section>
        </>
      )}

      {/* Mobile: detail Sheet, only renders below xl. */}
      <Sheet
        open={showingLatestRuns && mobileDetailOpen && resolved != null}
        onClose={() => setMobileDetailOpen(false)}
        side="bottom"
        width="lg:max-w-[1100px]"
      >
        <SheetHeader
          title={resolved?.scenario.name || resolved?.scenario.key || "Scenario"}
          subtitle={resolved?.skill.skill_name}
          onClose={() => setMobileDetailOpen(false)}
        />
        <SheetBody>
          {resolved && (
            <SkillTestingPanel
              key={`${resolved.skill.skill_id}-${resolved.scenario.id}-mobile`}
              skill={resolved.skill}
              scenario={resolved.scenario}
            />
          )}
        </SheetBody>
      </Sheet>
    </AppShell>
  )
}

function LatestRehearsalBlock({
  rehearsal, filter,
}: {
  rehearsal: RehearsalDetail
  filter: RehearsalFilter
}) {
  const skipped = rehearsal.skipped_count ?? 0
  const completed = rehearsal.passed_count + rehearsal.failed_count + rehearsal.errored_count + skipped
  const remaining = Math.max(rehearsal.total - completed, 0)
  const pending = rehearsal.pending_count ?? 0
  const runningCount = rehearsal.running_count ?? 0
  const target = rehearsal.target_skill_key
    ? `${rehearsal.target_skill_key} v${rehearsal.target_skill_version_number} draft`
    : "agent-wide live suite"
  const running = rehearsal.state === "queued" || rehearsal.state === "running"
  const progressText = running
    ? `${runningCount} running · ${pending} queued · ${remaining} remaining`
    : `finished ${fmtCompactRelative(rehearsal.finished_at)}`

  return (
    <section id={`rehearsal-${rehearsal.id}`} className="valet-panel overflow-hidden rounded-card">
      <div className="flex flex-col gap-4 border-b border-border p-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-app-text sm:text-xl">Latest rehearsal</h2>
            <Badge tone={rehearsalTone(rehearsal.state)}>{prettyState(rehearsal.state)}</Badge>
            {running && <Loader2 size={14} className="animate-spin text-app-muted" />}
          </div>
          <p className="mt-1 text-xs text-app-muted sm:text-sm">
            run #{rehearsal.id} · {target} · {progressText}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 text-[11px]">
          {running && <CountChip label="running" value={runningCount} tone="warning" />}
          {running && pending > 0 && <CountChip label="queued" value={pending} tone="neutral" />}
          <CountChip label="passed" value={rehearsal.passed_count} tone="success" />
          <CountChip label="failed" value={rehearsal.failed_count} tone="danger" />
          <CountChip label="errored" value={rehearsal.errored_count} tone="warning" />
          {skipped > 0 && <CountChip label="skipped" value={skipped} tone="neutral" />}
          <CountChip label="total" value={rehearsal.total} tone="neutral" />
        </div>
      </div>

      <div className="p-4">
        <RehearsalResultsTable rehearsal={rehearsal} filter={filter} />
      </div>
    </section>
  )
}

function CountChip({
  label, value, tone,
}: {
  label: string
  value: number
  tone: "success" | "danger" | "warning" | "neutral"
}) {
  return (
    <Badge tone={tone} className="gap-1">
      <span className="font-mono">{value}</span>
      <span>{label}</span>
    </Badge>
  )
}

function rehearsalTone(state: string): "success" | "danger" | "warning" | "neutral" {
  if (state === "passed") return "success"
  if (state === "failed") return "danger"
  if (state === "errored") return "warning"
  return "neutral"
}

function prettyState(state: string): string {
  return state.replace(/_/g, " ")
}

// Returns the (skillId, scenarioId) of the first failing scenario across
// the agent's suite — used to auto-select something useful on landing.
function firstFailingScenario(skills: SuiteHealthSkill[]): ScenarioSelection | null {
  for (const s of skills) {
    if (s.skill_id == null) continue
    for (const sc of s.scenarios) {
      if (sc.latest_status === "failed" || sc.latest_status === "errored") {
        return { skillId: s.skill_id, scenarioId: sc.id }
      }
    }
  }
  // Nothing failing — fall back to the first scenario with any run history.
  for (const s of skills) {
    if (s.skill_id == null) continue
    for (const sc of s.scenarios) {
      if (sc.latest_run_id != null) {
        return { skillId: s.skill_id, scenarioId: sc.id }
      }
    }
  }
  return null
}

function isActiveProgressState(status: string | null | undefined): boolean {
  return ACTIVE_PROGRESS_STATES.has(status ?? "")
}

function RunViewTabs({
  runView,
  onChange,
  latestRehearsal,
}: {
  runView: RunView
  onChange: (view: RunView) => void
  latestRehearsal: RehearsalDetail | null
}) {
  const rehearsalLabel = latestRehearsal
    ? `run #${latestRehearsal.id} · ${prettyState(latestRehearsal.state)}`
    : "no rehearsal yet"
  const tabs: Array<{ key: RunView; label: string; detail: string }> = [
    { key: "latest_runs", label: "All latest runs", detail: "latest result for every scenario" },
    { key: "latest_rehearsal", label: "Latest rehearsal", detail: rehearsalLabel },
  ]

  return (
    <div className="flex flex-wrap gap-2" aria-label="Run source">
      {tabs.map(({ key, label, detail }) => {
        const active = runView === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={cn(
              "rounded-control border px-4 py-2 text-left transition",
              active
                ? "border-brand-redBorder bg-brand-redSoft text-app-text"
                : "border-border bg-white/[.04] text-app-muted hover:text-app-text",
            )}
          >
            <span className="block text-sm font-semibold">{label}</span>
            <span className="block text-[11px] text-app-muted">{detail}</span>
          </button>
        )
      })}
    </div>
  )
}

function TestingTabs({
  tab, onChange, pendingCount,
}: {
  tab: ScenarioFilter
  onChange: (t: ScenarioFilter) => void
  pendingCount: number
}) {
  const tabs: Array<{ key: ScenarioFilter; label: React.ReactNode }> = [
    { key: "all",      label: "All" },
    { key: "failures", label: "Failures" },
    { key: "pending",  label: <>Drafts <span className="ml-1 rounded-full bg-white/[.10] px-1.5 py-0.5 text-[10px] text-app-muted">{pendingCount}</span></> },
  ]
  return (
    <div role="tablist" className="flex flex-wrap gap-2">
      {tabs.map(({ key, label }) => {
        const active = tab === key
        return (
          <button
            key={key}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(key)}
            className={cn(
              "rounded-control border px-4 py-2 text-sm transition",
              active
                ? "border-brand-redBorder bg-brand-redSoft font-semibold text-app-text"
                : "border-border bg-white/[.04] text-app-muted hover:text-app-text",
            )}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

function PendingPromotes({
  cards, busySkillIds, onRun, onPromote,
}: {
  cards: PendingPromoteCard[]
  busySkillIds: Set<number>
  onRun: (skillId: number) => void
  onPromote: (skillId: number) => void
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-app-muted">Drafts</h2>
      {cards.length === 0 ? (
        <Card className="p-6 text-sm text-app-muted">No drafts in flight.</Card>
      ) : (
        <div className="space-y-3">
          {cards.map((c) => (
            <PendingPromoteRow
              key={c.skill_id}
              card={c}
              isBusy={busySkillIds.has(c.skill_id)}
              onRun={() => onRun(c.skill_id)}
              onPromote={() => onPromote(c.skill_id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function PendingPromoteRow({
  card, isBusy, onRun, onPromote,
}: {
  card: PendingPromoteCard
  isBusy: boolean
  onRun: () => void
  onPromote: () => void
}) {
  const r = card.rehearsal
  const isRunning = r?.state === "queued" || r?.state === "running"
  const canRun = !isRunning && !isBusy
  const canPromote = card.per_skill_can_promote
  const shouldShowRun = !r || r.state === "failed" || r.state === "errored"

  return (
    <Card className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link href={card.test_lab_path} className="font-medium text-app-text hover:underline">
            {card.skill_name}
          </Link>
          {card.skill_key !== card.skill_name && (
            <code className="rounded bg-white/5 px-1 text-[11px] text-app-muted">{card.skill_key}</code>
          )}
          <Badge tone="red">v{card.draft_version_number} draft</Badge>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
          {card.per_skill_can_promote ? (
            <span className="inline-flex items-center gap-1 text-status-success">
              <CheckCircle2 size={12} /> per-skill checks pass
            </span>
          ) : (
            <BlockerCounts
              failing={card.per_skill_failing_count}
              needsFirstRun={card.per_skill_needs_first_run_count}
            />
          )}
          <span className="text-app-muted">·</span>
          <RehearsalChip rehearsal={r} isStarting={isBusy && !r} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {shouldShowRun && (
          <Button size="sm" variant="ghost" onClick={onRun} disabled={!canRun}>
            <Sparkles size={14} className="mr-1" />
            {isBusy ? "Starting…" : r ? "Re-run rehearsal" : "Run rehearsal"}
          </Button>
        )}
        {canPromote && (
          <Button size="sm" variant="red" onClick={onPromote} disabled={isBusy}>
            Promote draft
          </Button>
        )}
      </div>
    </Card>
  )
}

function BlockerCounts({ failing, needsFirstRun }: { failing: number; needsFirstRun: number }) {
  if (failing === 0 && needsFirstRun === 0) return null
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {failing > 0 && (
        <span className="inline-flex items-center gap-1 text-status-error">
          <XCircle size={12} /> {failing} failing
        </span>
      )}
      {needsFirstRun > 0 && (
        <span className="inline-flex items-center gap-1 text-status-warning">
          <AlertTriangle size={12} /> {needsFirstRun} need first run
        </span>
      )}
    </span>
  )
}

function RehearsalChip({
  rehearsal, isStarting,
}: {
  rehearsal: PendingPromoteCard["rehearsal"]
  isStarting: boolean
}) {
  if (isStarting) {
    return <span className="inline-flex items-center gap-1 text-app-muted"><Loader2 size={12} className="animate-spin" /> queueing</span>
  }
  if (!rehearsal) {
    return <span className="text-app-muted">no rehearsal yet</span>
  }
  if (rehearsal.state === "queued" || rehearsal.state === "running") {
    return (
      <span className="inline-flex items-center gap-1 text-app-muted">
        <Loader2 size={12} className="animate-spin" /> rehearsal {rehearsal.state}
      </span>
    )
  }
  if (rehearsal.state === "passed") {
    return (
      <span className="inline-flex items-center gap-1 text-status-success">
        <CheckCircle2 size={12} /> rehearsal {rehearsal.passed_count}/{rehearsal.total} passed
      </span>
    )
  }
  if (rehearsal.state === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-status-error">
        <XCircle size={12} /> rehearsal {rehearsal.failed_count} failed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-status-warning">
      <AlertTriangle size={12} /> rehearsal errored
    </span>
  )
}
